import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { RecaptchaComponent } from 'ng-recaptcha';
import { LeadService } from '../../shared/services/lead.service';
import { Lead } from '../../shared/models/lead';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { HelperService } from '../../shared/services/helper.service';

@Component({
  templateUrl: './create-request.component.html'
})
export class CreateRequestComponent implements OnInit {
  @ViewChild('captchaRef')
  public captchaRef!: RecaptchaComponent;

  public lead: Lead = {
    state: '',
    requestedDataTypes: [],
    lawyer: { lawFirm: {} }
  };
  public reportTypeName = '';
  public siteKey = environment.reCaptchaSiteKey;
  public isLoading = false;
  public validationErrors: string[] = [];

  public allStates: { code: string, name: string }[] = [
    { code: 'AL', name: 'Alabama' },
    { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' },
    { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' },
    { code: 'DE', name: 'Delaware' },
    { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' },
    { code: 'HI', name: 'Hawaii' },
    { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' },
    { code: 'IN', name: 'Indiana' },
    { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' },
    { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' },
    { code: 'MD', name: 'Maryland' },
    { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' },
    { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' },
    { code: 'MT', name: 'Montana' },
    { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' },
    { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' },
    { code: 'NY', name: 'New York' },
    { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' },
    { code: 'OH', name: 'Ohio' },
    { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' },
    { code: 'PA', name: 'Pennsylvania' },
    { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' },
    { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' },
    { code: 'UT', name: 'Utah' },
    { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' },
    { code: 'WA', name: 'Washington' },
    { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' },
    { code: 'WY', name: 'Wyoming' },
    { code: '01', name: 'Federal' }
  ];

  public allDataTypes = [
    { id: 1, name: 'UCR' },
    { id: 2, name: 'Medicare' },
    { id: 3, name: 'Health Insurance' },
    { id: 4, name: 'NJ PIP' }
  ];

  public filteredDataTypes: { id: number, name: string }[] = [];

  constructor(
    private readonly _leadService: LeadService,
    private readonly _route: ActivatedRoute,
    private readonly _router: Router,
    private readonly _helper: HelperService
  ) {

  }

  public ngOnInit(): void {
    this._route.paramMap.subscribe(params => {
      const reportType = Number(params.get('reportType')) || 1;
      this.lead.reportType = reportType;
      this.reportTypeName = this.getReportTypeName(reportType);
      this.onStateChange()
    });
  }
  public runCaptcha(form: NgForm): void {
    if (form.valid) {
      this.captchaRef.execute();
    }
  }

  public async onCaptchaResolved(token: string | null): Promise<void> {
    if (!token) {
      console.warn('CAPTCHA returned null token');
      return;
    }

    this.lead.captchaResponse = token;
    await this.submitForm();
  }

  private async submitForm(): Promise<void> {
    try {
      this.isLoading = true;
      this.validationErrors = [];

      await this._leadService.createLead(this.lead);

      this._router.navigate(['/']);
      this._leadService.leadCreated$.next();
    } catch (e) {
      if (this._helper.parseValidationErrors(e, this.validationErrors)) {
        throw e;
      }
    } finally {
      this.captchaRef.reset();
      this.isLoading = false;
    }
  }

  private getReportTypeName(type: number): string {
    const map: { [key: number]: string } = {
      1: 'Basic UCR Report',
      2: 'Basic Medicare Report',
      3: 'Basic Combo Report',
      4: 'Comprehensive Case Review',
      5: 'Causation Necessity and Reasonableness',
      6: 'Billing Summary',
      7: 'Medical Chronology',
      8: 'Medical Cost / Project Cost Projection'
    };
    return map[type] ?? 'Unknown Report';
  }

  public onStateChange(): void {
    const state = this.lead.state;
    const allowedIds = state === 'FL' ? [1, 2, 3] : state === 'NJ' ? [1, 4] : [];
    this.filteredDataTypes = this.allDataTypes
      .filter(d => allowedIds.includes(d.id));
    this.lead.requestedDataTypes = [];
  }
}
